import React from 'react'
import styled, { useTheme } from 'styled-components'

import AlertSvg from '@/assets/svgs/alert-fill.svg?react'
import CloseSvg from '@/assets/svgs/close-line.svg?react'
import WarningSvg from '@/assets/svgs/warning-fill.svg?react'
import { BodyM } from '@/components/Text.tsx'

export enum NotificationStatus {
  SUCCESS,
  ERROR,
  WARNING,
  INFO,
}

const useNotificationTheme = (type: NotificationStatus) => {
  const theme = useTheme()

  switch (type) {
    case NotificationStatus.SUCCESS:
      return {
        backgroundColor: theme.colors.common.mediumSeaGreenLight,
        color: theme.colors.common.mediumSeaGreen,
      }
    case NotificationStatus.ERROR:
      return {
        backgroundColor: theme.colors.common.alertLight,
        color: theme.colors.common.alert,
      }
    case NotificationStatus.WARNING:
      return {
        backgroundColor: theme.colors.common.warningLight,
        color: theme.colors.common.warning,
      }
    case NotificationStatus.INFO:
      return {
        backgroundColor: theme.colors.common.periwinkleExtraLight,
        color: theme.colors.common.accentDark,
      }
  }
}

const StyledNotification = styled.div<{
  $notificationTheme: {
    backgroundColor: string
    color: string
  }
}>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-around;
  padding: 20px 24px;
  z-index: ${({ theme }) => theme.zIndex.notification};

  background-color: ${({ $notificationTheme }) =>
    $notificationTheme.backgroundColor};
  border-top: 4px solid ${({ $notificationTheme }) => $notificationTheme.color};

  svg path {
    fill: ${({ $notificationTheme }) => $notificationTheme.color};
  }
`

export const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  flex: none;
  border-radius: 12px;

  &:hover,
  &:active {
    background-color: rgba(0, 0, 0, 0.1);
  }
`

const getIconByType = (type: NotificationStatus) => {
  switch (type) {
    case NotificationStatus.SUCCESS:
    case NotificationStatus.INFO:
      return <AlertSvg />
    case NotificationStatus.WARNING:
    case NotificationStatus.ERROR:
      return <WarningSvg />
  }
}
export const Icon = styled.img`
  flex: none;
`

export const Label = styled(BodyM)`
  padding: 0 12px;
  flex: 1;
`

type NotificationProps = {
  label: string
  onClose?: () => void
  type: NotificationStatus
}

const Notification = ({
  label,
  onClose = undefined,
  type,
}: NotificationProps): React.ReactElement => {
  const theme = useNotificationTheme(type)
  return (
    <StyledNotification $notificationTheme={theme}>
      {getIconByType(type)}
      <Label>{label}</Label>
      {onClose && (
        <CloseButton onClick={onClose}>
          <CloseSvg />
        </CloseButton>
      )}
    </StyledNotification>
  )
}

type NotificationComponentProps = Omit<NotificationProps, 'type'>
const NotificationSuccess = (
  props: NotificationComponentProps,
): React.ReactElement => (
  <Notification {...props} type={NotificationStatus.SUCCESS} />
)
const NotificationWarning = (
  props: NotificationComponentProps,
): React.ReactElement => (
  <Notification {...props} type={NotificationStatus.WARNING} />
)
const NotificationInfo = (
  props: NotificationComponentProps,
): React.ReactElement => (
  <Notification {...props} type={NotificationStatus.INFO} />
)
const NotificationError = (
  props: NotificationComponentProps,
): React.ReactElement => (
  <Notification {...props} type={NotificationStatus.ERROR} />
)

export {
  NotificationError,
  NotificationInfo,
  NotificationSuccess,
  NotificationWarning,
}
